import { 
  Box, 
  Card, 
  CardActionArea, 
  CardContent, 
  Grid, 
  Typography,
  Container,
  Stack,
  Chip,
  Alert,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  NotificationImportant,
  TrendingUp,
  CheckCircle,
  Engineering,
  Warning,
  EmojiEvents,
} from '@mui/icons-material';

export default function AnalyticsIndex() {
  const navigate = useNavigate();

  const primaryCards = [
    { 
      title: '🚨 Attention Needed', 
      desc: 'Pages with issues requiring immediate action - anomalies, declining momentum, or performance problems',
      path: '/analytics/attention',
      icon: <NotificationImportant sx={{ fontSize: 48, opacity: 0.7 }} />,
      color: 'error.light',
      badge: 'Priority',
    },
    { 
      title: '💡 Growth Opportunities', 
      desc: 'High-potential pages with clear paths to improvement - quick wins, SERP features, and content gaps',
      path: '/analytics/opportunities',
      icon: <EmojiEvents sx={{ fontSize: 48, opacity: 0.7 }} />,
      color: 'success.light',
      badge: 'Recommended',
    },
    { 
      title: '✅ What\'s Working', 
      desc: 'Pages performing well with positive momentum, strong competitive positioning, and recent improvements',
      path: '/analytics/success',
      icon: <CheckCircle sx={{ fontSize: 48, opacity: 0.7 }} />,
      color: 'info.light',
      badge: null,
    },
  ];

  const technicalCards = [
    { title: 'Anomalies', desc: 'Statistical analysis of unusual metric changes (Z-score detection)', path: '/analytics/anomalies', technical: true },
    { title: 'OCI Scores', desc: 'Observed Competitiveness Index - competitive strength analysis', path: '/analytics/oci', technical: true },
    { title: 'Momentum Trends', desc: 'Rising and falling trend analysis across time periods', path: '/analytics/momentum', technical: true },
    { title: 'SERP Features', desc: 'Monitor SERP feature ownership and opportunities', path: '/analytics/serp', technical: true },
    { title: 'GA4 Integration', desc: 'Behavioral metrics blended with search signals', path: '/analytics/ga4', technical: true },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor page performance, discover opportunities, and identify issues
          </Typography>
        </Box>

        <Alert severity="info" icon={<Warning />}>
          <Typography variant="body2">
            <strong>New User-Friendly View:</strong> We've reorganized analytics to focus on actionable insights. 
            Use "Attention Needed" for problems, "Growth Opportunities" for improvements, and "What's Working" for successes.
          </Typography>
        </Alert>

        {/* Primary Analytics - User Focused */}
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            📊 Main Analytics
          </Typography>
          <Grid container spacing={3}>
            {primaryCards.map((card) => (
              <Grid item xs={12} md={4} key={card.title}>
                <Card 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    '&:hover': { boxShadow: 6 }
                  }}
                >
                  <CardActionArea 
                    onClick={() => navigate(card.path)}
                    sx={{ height: '100%', p: 2 }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        {card.badge && (
                          <Chip 
                            label={card.badge} 
                            size="small" 
                            color="primary"
                            sx={{ width: 'fit-content' }}
                          />
                        )}
                        
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="h5" component="h3">
                            {card.title}
                          </Typography>
                          {card.icon}
                        </Stack>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60 }}>
                          {card.desc}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Technical Analytics - For SEO Engineers */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Engineering />
            <Typography variant="h6">
              Advanced Analytics (SEO Engineers)
            </Typography>
          </Stack>
          
          <Grid container spacing={2}>
            {technicalCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.title}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardActionArea onClick={() => navigate(card.path)}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {card.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {card.desc}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Container>
  );
}
